<script setup>
// Menu rápido para qualificar/mover um lead já vinculado à conversa atual.
// Permite: mover stage (sem sair), marcar ganho/perdido, trocar classificação,
// abrir drawer completo.
import { ref, computed } from 'vue';
import LeadsAPI from 'dashboard/api/leads';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  lead: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['updated']);

const isOpen = ref(false);
const isWorking = ref(false);

const pipelineUrl = computed(() => {
  if (!props.lead?.pipeline?.id) return null;
  return `/app/accounts/${props.accountId}/pipelines/${props.lead.pipeline.id}?lead=${props.lead.id}`;
});

function close() {
  isOpen.value = false;
}

async function setClassification(value) {
  if (isWorking.value) return;
  isWorking.value = true;
  try {
    const { data } = await LeadsAPI.update(props.lead.id, { lead: { classification: value } });
    emit('updated', data);
    useAlert(`Classificação atualizada para ${labelOf(value)}.`);
    close();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao classificar.');
  } finally {
    isWorking.value = false;
  }
}

async function moveStage(stageId) {
  if (isWorking.value) return;
  isWorking.value = true;
  try {
    const { data } = await LeadsAPI.moveStage(props.lead.id, { stage_id: stageId });
    emit('updated', data);
    useAlert('Estágio atualizado.');
    close();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao mover estágio.');
  } finally {
    isWorking.value = false;
  }
}

function labelOf(c) {
  return { hot: 'Quente', warm: 'Morno', cold: 'Frio' }[c] || c;
}

const stages = computed(() => {
  // Lead já vem com pipeline (jbuilder), mas stages do pipeline pode não estar.
  // Em produção, frontend já carrega pipelines via store; aqui simplificamos
  // mostrando só "abrir drawer" como fallback.
  return props.lead?.pipeline?.stages || [];
});
</script>

<template>
  <div
    v-on-click-outside="close"
    class="relative inline-block"
  >
    <!-- Trigger autônomo (proeminente) - usado quando o componente vive ao lado
         do botão "Abrir" no header da conversa. -->
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-body-small font-medium border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
      v-tooltip="'Ações rápidas do lead'"
      @click="isOpen = !isOpen"
    >
      <i class="i-lucide-target size-3.5 text-n-brand" />
      Lead
      <i class="i-lucide-chevron-down size-3 text-n-slate-11" />
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 top-full mt-1 z-30 w-56 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
    >
      <!-- Classificação -->
      <div class="px-2 py-1.5">
        <p class="text-label-small text-n-slate-10 uppercase tracking-wide mb-1.5">Classificar</p>
        <div class="flex gap-1">
          <button
            v-for="c in ['hot', 'warm', 'cold']"
            :key="c"
            type="button"
            class="flex-1 px-2 py-1 text-label-small font-medium rounded transition-colors"
            :class="lead.classification === c
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
            @click="setClassification(c)"
          >
            {{ labelOf(c) }}
          </button>
        </div>
      </div>

      <div class="border-t border-n-weak my-1" />

      <!-- Atalhos -->
      <router-link
        v-if="pipelineUrl"
        :to="pipelineUrl"
        class="flex items-center gap-2 px-3 py-2 text-body-small text-n-slate-12 hover:bg-n-alpha-1"
        @click="close"
      >
        <i class="i-lucide-external-link size-3.5 text-n-slate-11" />
        Abrir detalhes do lead
      </router-link>
    </div>
  </div>
</template>
